const flagshipServices = [
  {
    id: "whatsapp-cloud-api",
    icon: "bi-whatsapp",
    href: "https://wa.odinno.com",
    tag: "Official WhatsApp Cloud API",
    title: "WhatsApp Cloud API & Multi-Tenant Proxy Portals",
    description:
      "Fully managed, production-grade WhatsApp Business API integration — including white-label multi-tenant proxy portals that let you serve dozens of client numbers from a single secure platform.",
    features: [
      "Official Cloud API onboarding & number migration",
      "Multi-tenant proxy portal with role-based access",
      "Template management & message approval workflows",
      "End-to-end delivery receipts & analytics",
    ],
  },
  {
    id: "ai-chatbots",
    icon: "bi-robot",
    tag: "Conversational AI & Commerce",
    title: "AI Chatbots & Conversational Commerce",
    description:
      "Intelligent WhatsApp and website chatbots that qualify leads, answer product questions, process orders and book appointments — turning every conversation into revenue.",
    features: [
      "Intent recognition & natural language flows",
      "Product catalogs, carts & payment links",
      "Human handoff with full conversation history",
      "Multilingual bot personalities",
    ],
  },
  {
    id: "broadcast-messaging",
    icon: "bi-broadcast",
    href: "https://wa.odinno.com",
    tag: "Messaging at Scale",
    title: "Broadcast Messaging & Transactional Alerts",
    description:
      "Segment-driven broadcast campaigns and reliable transactional alerts — order updates, OTPs, payment confirmations and reminders — delivered with precision and full compliance.",
    features: [
      "Audience segmentation & scheduling",
      "Transactional OTP & notification delivery",
      "Opt-in / opt-out consent management",
      "Detailed delivery & performance reporting",
    ],
  },
  {
    id: "meta-ads-automation",
    icon: "bi-megaphone",
    tag: "Meta Ads API & Automation",
    title: "Meta Ads Webhooks & Marketing Automation",
    description:
      "Programmatic ad management through the Meta Ads API with real-time webhook workflows, automated audience building and conversion-driven optimization at scale.",
    features: [
      "Automated campaign creation & bidding",
      "Real-time lead & event webhooks",
      "Custom audiences & retargeting pipelines",
      "Dashboards with live ROAS insights",
    ],
  },
];

const enterpriseSystems = [
  {
    id: "erp",
    icon: "bi-boxes",
    title: "Custom ERP Systems",
    description:
      "End-to-end enterprise resource planning built around your exact workflows — from procure-to-pay to order-to-cash, with full visibility across departments.",
    features: [
      "Supply chain & procurement management",
      "Multi-branch inventory & warehouse control",
      "Production planning & bill of materials",
      "Role-based dashboards & audit trails",
    ],
  },
  {
    id: "crm",
    icon: "bi-people",
    title: "CRM & Lead Management Pipelines",
    description:
      "360-degree customer tracking and sales automation that gives your team one source of truth — from first touch to repeat business.",
    features: [
      "Visual pipeline & deal stage automation",
      "Activity tracking, notes & follow-up reminders",
      "Lead scoring, routing & assignment rules",
      "Revenue forecasts & conversion analytics",
    ],
  },
  {
    id: "hrms",
    icon: "bi-person-badge",
    title: "HRMS & Payroll Automation",
    description:
      "A complete human resource suite that automates the employee lifecycle and eliminates payroll errors.",
    features: [
      "Biometric attendance & shift management",
      "Leave, claims & expense workflows",
      "Automated payroll with tax calculations",
      "Employee self-service portal & payslips",
    ],
  },
  {
    id: "finance",
    icon: "bi-cash-coin",
    title: "Financial & Account Management Systems",
    description:
      "Robust accounting platforms for accurate books, compliant reporting and confident financial decisions.",
    features: [
      "General ledger, chart of accounts & journals",
      "Invoicing, receivables & payables",
      "Expense tracking & tax compliance",
      "Immutable audit logs & fiscal year close",
    ],
  },
];

const industrySolutions = [
  {
    id: "ecommerce",
    icon: "bi-bag-check",
    name: "E-Commerce & Multi-Vendor Marketplaces",
    shortName: "E-Commerce",
    description:
      "Modern headless commerce platforms and marketplace ecosystems that scale with your catalog, vendors and buyers.",
    solutions: [
      "Headless storefronts with React / Next.js",
      "Vendor portals with commission & split payments",
      "AI-powered cart recovery via WhatsApp",
      "PIM, order & inventory orchestration",
    ],
  },
  {
    id: "healthtech",
    icon: "bi-heart-pulse",
    name: "Healthcare & Hospital Management",
    shortName: "HealthTech",
    description:
      "Digital healthcare systems that improve patient care, streamline hospital operations and keep data secure.",
    solutions: [
      "Hospital Information Systems (HIS)",
      "EMR / EHR with clinical decision support",
      "Telemedicine video consultation portals",
      "HIPAA / GDPR-compliant data handling",
    ],
  },
  {
    id: "proptech",
    icon: "bi-building",
    name: "Real Estate & Property Portals",
    shortName: "PropTech",
    description:
      "Property technology that connects brokers, tenants and investors through intelligent, automated platforms.",
    solutions: [
      "Tenant billing & lease management portals",
      "3D virtual tours & property listings",
      "Real estate CRMs & pipeline tools",
      "Automated lead routing & follow-ups",
    ],
  },
  {
    id: "fintech",
    icon: "bi-credit-card",
    name: "Financial Technology & Banking",
    shortName: "FinTech",
    description:
      "Secure, high-volume financial platforms engineered for trust, speed and regulatory readiness.",
    solutions: [
      "Custom payment gateways & integrations",
      "Digital wallets & instant transfers",
      "Loan origination & credit scoring engines",
      "Automated KYC / AML onboarding flows",
    ],
  },
  {
    id: "edtech",
    icon: "bi-mortarboard",
    name: "Education & Campus Management",
    shortName: "EdTech",
    description:
      "Campus-wide platforms that enhance learning, administration and student engagement.",
    solutions: [
      "Learning Management Systems (LMS)",
      "Student & faculty self-service portals",
      "Online exams with proctoring analytics",
      "Automated fee collection & reminders",
    ],
  },
  {
    id: "legaltech",
    icon: "bi-bank",
    name: "Legal & Law Firm Management",
    shortName: "LegalTech",
    description:
      "Practice management software that keeps firms organized, billable and audit-ready.",
    solutions: [
      "Case & matter tracking repositories",
      "Automated contract e-signatures",
      "Billable hours & retainer invoicing",
      "Document vaults with access control",
    ],
  },
  {
    id: "isp-telecom",
    icon: "bi-router",
    name: "ISP & Telecom Automation",
    shortName: "ISP & Telecom",
    description:
      "Automation suites that run internet service providers efficiently — from provisioning to billing to support.",
    solutions: [
      "Subscriber billing & portal automation",
      "MikroTik / FreeRADIUS integrations",
      "Bandwidth & usage controls",
      "Outage broadcast alerts via WhatsApp",
    ],
  },
];

export { flagshipServices, enterpriseSystems, industrySolutions };
